<template>
  <VModal :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" :click-to-close="!isLoading" :esc-to-close="!isLoading" @opened="handleModalOpened" @closed="handleModalClosed" size="dialog" id="google-status-modal" :z-index="9999" :dynamic-width="true" content-class="max-w-lg w-full">
    <!-- Modal icon -->
    <template #icon>
      <div class="flex items-center justify-center h-10 w-10 rounded-full bg-red-50">
        <span class="material-symbols-outlined text-red-500 text-2xl">
          {{ currentView === 'accounts' ? 'account_circle' : 'analytics' }}
        </span>
      </div>
    </template>

    <!-- Modal header -->
    <template #header-content>
      <div class="flex flex-col">
        <h2 class="text-xl font-bold text-gray-800">
          {{ currentView === 'accounts' ? 'Google Hesap Seçimi' : 'Analytics Mülk Seçimi' }} </h2>
        <p class="text-sm text-gray-500 mt-1">
          {{
            currentView === 'accounts'
                ? 'Google hesabınızı seçerek Analytics verilerine erişin'
                : 'İzlemek istediğiniz Analytics mülkünü seçin'
          }} </p>
      </div>
    </template>

    <!-- Google Status Modal content -->
    <div class="p-4">
      <!-- Loading state -->
      <div v-if="isLoadingAccounts || isLoadingProperties" class="flex flex-col items-center justify-center py-12">
        <div class="relative w-16 h-16 mb-4">
          <div class="absolute top-0 left-0 w-full h-full border-4 border-primary border-opacity-25 rounded-full"></div>
          <div class="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-primary rounded-full animate-spin"></div>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="material-symbols-outlined text-primary text-xl">{{ currentView === 'accounts' ? 'account_circle' : 'analytics' }}</span>
          </div>
        </div>
        <p class="mt-3 text-gray-700 font-medium text-center">
          {{
            currentView === 'accounts'
                ? 'Google Mail hesaplarınız yükleniyor...'
                : 'Google Analytics mülkleriniz yükleniyor...'
          }} </p>
      </div>

      <!-- Error state -->
      <div v-else-if="currentView === 'accounts' && accountError" class="bg-red-50 border border-red-100 p-4 mt-3 rounded-xl shadow-sm">
        <div class="flex items-start">
          <div class="flex-shrink-0 p-1">
            <span class="material-symbols-outlined text-red-500">error</span>
          </div>
          <div class="ml-3">
            <h4 class="font-medium text-red-800 text-sm mb-1">Hesaplar yüklenirken hata oluştu</h4>
            <p class="text-red-700 text-sm">{{ accountError }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="currentView === 'properties' && propertyError" class="bg-red-50 border border-red-100 p-4 mt-3 rounded-xl shadow-sm">
        <div class="flex items-start">
          <div class="flex-shrink-0 p-1">
            <span class="material-symbols-outlined text-red-500">error</span>
          </div>
          <div class="ml-3">
            <h4 class="font-medium text-red-800 text-sm mb-1">Mülkler yüklenirken hata oluştu</h4>
            <p class="text-red-700 text-sm">{{ propertyError }}</p>
          </div>
        </div>
      </div>

      <!-- No mail accounts found -->
      <div v-else-if="currentView === 'accounts' && googleAccounts.length === 0" class="text-center py-10">
        <div class="w-24 h-24 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-6 border-2 border-primary/10">
          <span class="material-symbols-outlined text-primary text-5xl">account_circle</span>
        </div>
        <p class="text-xl font-medium text-gray-800 mb-3">
          Kayıtlı Google Hesabı Bulunamadı </p>
        <p class="text-gray-600 mb-8 max-w-md mx-auto">
          Google hesabınızla ilişkilendirilmiş herhangi bir Mail hesabı bulunmamaktadır. Yeni bir Google hesabı ekleyerek başlayabilirsiniz. </p>

        <div class="flex justify-center">
          <VButton @click="connectToGoogle" color="primary" size="md" :button-text="'Google Hesabı Ekle'" icon="add_circle" :show-icon="true"/>
        </div>
      </div>

      <!-- No properties found -->
      <div v-else-if="currentView === 'properties' && googleProperties.length === 0" class="text-center py-10">
        <div class="w-24 h-24 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-6 border-2 border-primary/10">
          <span class="material-symbols-outlined text-primary text-5xl">analytics</span>
        </div>
        <p class="text-xl font-medium text-gray-800 mb-3">
          Analytics Mülkü Bulunamadı </p>
        <p class="text-gray-600 mb-8 max-w-md mx-auto">
          Google hesabınızla ilişkilendirilmiş herhangi bir Analytics mülkü bulunmamaktadır. Google Analytics'te bir mülk oluşturup tekrar deneyebilirsiniz. </p>

        <div class="flex justify-center space-x-4">
          <VButton @click="goBackToAccounts" color="neutral" size="md" variation="outline" :button-text="'Hesaba Geri Dön'" icon="arrow_back" :show-icon="true"/>
          <VButton @click="connectToGoogle" color="primary" size="md" :button-text="'Google Hesabı Ekle'" icon="add_circle" :show-icon="true"/>
        </div>
      </div>

      <!-- Mail accounts list -->
      <div v-else-if="currentView === 'accounts' && googleAccounts.length > 0" class="px-2">
        <div class="mb-3">
          <div class="text-gray-800 text-sm font-medium mb-1.5">
            Lütfen kullanmak istediğiniz Google hesabını seçin:
          </div>
          <div class="border-t border-gray-200 relative pb-6">
            <div class="inline-flex items-center text-xs text-gray-500 absolute -top-[9px] bg-white px-2 left-2">
              <span class="material-symbols-outlined text-gray-400 !text-xs mr-1">info</span> <span>Hesap silmek için avatar ikonunun üzerine gelin</span>
            </div>
          </div>
        </div>

        <!-- Hesaplar listesi - temiz ve sade tasarım -->
        <div class="mb-4 border border-gray-200 rounded-lg overflow-hidden">
          <!-- Liste içeriği -->
          <div class="max-h-[360px] overflow-y-auto thin-scrollbar">
            <div v-for="account in googleAccounts" :key="account.id" class="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors duration-200" :class="{'bg-gray-50': selectedAccountId === account.id}" @click="toggleAccountSelection(account.id)">

              <div class="flex items-center p-3 cursor-pointer">
                <!-- Avatar ikonu - hover'da flip animasyonu ile silme ikonu -->
                <div class="relative flip-container h-9 w-9 group mr-3 flex-shrink-0 cursor-pointer" @click.stop="showRevokeDialog(account)">
                  <div class="flipper">
                    <div class="front rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 h-9 w-9">
                      <span class="material-symbols-outlined text-gray-500 !text-[18px]">person</span>
                    </div>
                    <div class="back rounded-full bg-red-100 flex items-center justify-center overflow-hidden border border-red-200 h-9 w-9">
                      <span class="material-symbols-outlined text-red-500 !text-[18px]">delete</span>
                    </div>
                  </div>
                </div>

                <!-- Mail ve isim -->
                <div class="flex-1 min-w-0 overflow-hidden">
                  <div class="font-medium text-gray-700 truncate">{{ account.email }}</div>
                  <div v-if="account.name" class="text-xs text-gray-500 truncate">{{ account.name }}</div>
                </div>

                <!-- Seçim göstergesi -->
                <div class="ml-3 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" :class="selectedAccountId === account.id ? 'bg-red-500 text-white' : 'border border-gray-300'">
                  <span v-if="selectedAccountId === account.id" class="material-symbols-outlined !text-xs">check</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- "Yeni Google Hesabı Ekle" butonu -->
        <div class="mt-4 flex justify-center">
          <button @click="addNewGoogleAccount" class="flex items-center justify-center px-3 py-1.5 text-sm font-medium text-red-500 border border-red-200 bg-white rounded-md hover:bg-red-50 transition-colors">
            <span class="material-symbols-outlined text-red-500 mr-1.5 !text-sm">add</span> Yeni Google Hesabı Ekle
          </button>
        </div>
      </div>

      <!-- Properties list -->
      <div v-else-if="currentView === 'properties' && googleProperties.length > 0" class="px-2">
        <p class="mb-4 text-gray-600 text-sm">
          Lütfen Google Analytics verilerini almak istediğiniz mülkü seçin: </p>

        <div class="overflow-y-auto max-h-[380px] border border-gray-200 rounded-lg thin-scrollbar">
          <div v-for="property in googleProperties" :key="property.id" class="border-b border-gray-100 last:border-b-0">
            <div class="flex items-center py-3 px-4 cursor-pointer relative hover:bg-gray-50/70" :class="{'bg-primary/5': selectedPropertyId === property.id}" @click="selectProperty(property.id)">
              <div v-if="selectedPropertyId === property.id" class="absolute left-0 top-0 bottom-0 w-1 bg-primary"></div>

              <!-- Left side: Property info -->
              <div class="flex items-center space-x-3 flex-1">
                <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/10">
                  <span class="material-symbols-outlined">analytics</span>
                </div>

                <div class="flex-1 min-w-0">
                  <h3 class="font-medium text-gray-900 truncate">
                    {{ property.displayName }} </h3>
                  <div class="flex items-center">
                    <span class="material-symbols-outlined text-gray-400 text-[12px] mr-1">link</span>
                    <p class="text-xs text-gray-500 truncate">
                      {{ property.url || 'URL bilgisi yok' }} </p>
                  </div>
                </div>
              </div>

              <!-- Right side: Selection icon -->
              <div class="ml-auto">
                <div v-if="selectedPropertyId === property.id" class="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white">
                  <span class="material-symbols-outlined text-lg">check</span>
                </div>
                <div v-else class="flex items-center justify-center w-7 h-7 rounded-full border border-gray-200 text-gray-400">
                  <span class="material-symbols-outlined text-lg">radio_button_unchecked</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alt butonlar -->
    <template v-if="currentView === 'accounts' && googleAccounts.length > 0" #footer>
      <div class="flex justify-between items-center">
        <!-- İptal butonu -->
        <VButton @click="closeGoogleStatusModal" color="neutral" size="md" variation="outline" :button-text="'İptal'" icon="close" :show-icon="true"/>

        <!-- Devam Et butonu -->
        <VButton @click="accountSelected" color="primary" size="md" :is-disabled="!selectedAccountId || isProcessingAccountSelection" :is-loading="isProcessingAccountSelection" :button-text="'Devam Et'" :loading-text="'İşleniyor'" icon="arrow_forward" :show-icon="true"/>
      </div>
    </template>

    <!-- Action buttons - Properties view -->
    <template v-if="currentView === 'properties' && googleProperties.length > 0" #footer>
      <div class="flex justify-between">
        <VButton @click="goBackToAccounts" color="neutral" size="md" variation="outline" :button-text="'Geri'" icon="arrow_back" :show-icon="true"/>

        <VButton @click="savePropertySelection" color="primary" size="md" :is-disabled="!selectedPropertyId || isSavingProperty" :is-loading="isSavingProperty" :button-text="'Seçimi Kaydet'" :loading-text="'Kaydediliyor'" icon="save" :show-icon="true"/>
      </div>
    </template>
  </VModal>

  <!-- Hesap Silme Diyaloğu - Özelleştirilmiş İçerik ile -->
  <VDialog v-model="showDeleteAccountModal" type="delete" title="Google Hesabı Erişimini Kaldır" :loading="isDeletingAccount" :confirm-button-text="isDeletingAccount ? 'İşleniyor...' : 'Erişimi Kaldır'" :cancel-button-text="'İptal'" :showCancelButton="true" icon="delete_forever" :icon-class="'text-red-600'" :is-nested-modal="true" :useContentSlot="true" content-class="max-w-md" @confirm="confirmDeleteAccount">
    <div class="space-y-4">
      <!-- Hesap Bilgileri - Daha minimal görünüm -->
      <div class="flex items-center border-b border-gray-100 pb-4">
        <div class="w-9 h-9 rounded-full bg-red-50 flex-shrink-0 flex items-center justify-center">
          <span class="material-symbols-outlined text-red-500 !text-base">account_circle</span>
        </div>
        <div class="ml-3 flex-1">
          <p class="font-medium text-gray-800">
            {{ accountToDelete?.email || 'Seçili Google hesabı' }} </p>
          <p v-if="accountToDelete?.name" class="text-xs text-gray-500">{{ accountToDelete.name }}</p>
        </div>
      </div>

      <!-- Onay Sorusu - Sade ve net -->
      <p class="text-gray-700 text-sm">Bu hesabın erişimini kaldırmak istediğinize emin misiniz?</p>

      <!-- Uyarı - Temiz ve minimal -->
      <div class="flex items-start bg-gray-50 p-3 rounded-md">
        <span class="material-symbols-outlined text-gray-500 !text-sm mr-2">info</span>
        <div class="text-xs text-gray-600">
          <p>Bu işlem geri alınamaz. Hesaba tekrar bağlanmanız gerekecektir.</p>
        </div>
      </div>
    </div>
  </VDialog>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'
import VModal from '@/components/modals/VModal.vue'
import VButton from '@/components/base/buttons/VButton.vue'
import VDialog from '@/components/feedback/VDialog/VDialog.vue'
import { useAssetStore } from '@/stores/asset'
import useGoogleIntegration from '../../composables/use-google-integration'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['update:modelValue'])

const assetStore = useAssetStore()

// Helpers

// Get all Google integration state and methods
const {
  // State
  googleAccounts,
  googleProperties,
  isLoadingAccounts,
  isLoadingProperties,
  accountError,
  propertyError,
  selectedAccountId,
  selectedPropertyId,
  currentView,
  isProcessingAccountSelection,
  isSavingProperty,

  // Methods
  loadGoogleAccounts,
  loadGoogleProperties,
  selectAccount,
  selectProperty,
  accountSelected,
  savePropertySelection,
  goBackToAccounts,
  connectToGoogle,
  addNewGoogleAccount,
  closeGoogleStatusModal,
  revokeAccount,
} = useGoogleIntegration()

// Delete account state
const showDeleteAccountModal = ref(false)
const isDeletingAccount = ref(false)
const accountToDelete = ref(null)

/**
 * Shows the revoke confirmation dialog for an account
 * @param {Object} account - The account to revoke
 */
const showRevokeDialog = (account) => {
  accountToDelete.value = account
  showDeleteAccountModal.value = true
}

/**
 * Toggles account selection
 * @param {string|null} accountId - The account ID to select, or null to deselect
 */
const toggleAccountSelection = (accountId) => {
  // If the selected account is clicked again, deselect it
  if (selectedAccountId.value === accountId) {
    selectedAccountId.value = null
  } else {
    selectAccount(accountId)
  }
}

/**
 * Confirms and executes account deletion
 */
const confirmDeleteAccount = async () => {
  if (!accountToDelete.value) return

  isDeletingAccount.value = true

  try {
    await revokeAccount(accountToDelete.value.id, assetStore.assetId)

    // Reload accounts after revocation
    await loadGoogleAccounts()

    // Reset selected account if it was revoked
    if (selectedAccountId.value === accountToDelete.value.id) {
      selectedAccountId.value = null
    }

    // Close modal if no accounts left
    if (googleAccounts.value.length === 0) {
      closeGoogleStatusModal()
    }
  } finally {
    isDeletingAccount.value = false
    showDeleteAccountModal.value = false
    accountToDelete.value = null
  }
}


const handleModalOpened = () => {
  // Actions when modal is opened
}

const handleModalClosed = () => {
  // Reset state when modal is closed
  currentView.value = 'accounts'
  selectedAccountId.value = null
  selectedPropertyId.value = null
}
</script>

<style scoped>
/* Flip animasyonu */
.flip-container {
  perspective: 1000px;
}

.flip-container:hover .flipper {
  transform: rotateY(180deg);
}

.flipper {
  transition: transform 0.6s;
  transform-style: preserve-3d;
  position: relative;
  width: 100%;
  height: 100%;
}

.front, .back {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.front {
  z-index: 2;
  transform: rotateY(0deg);
}

.back {
  transform: rotateY(180deg);
}
</style>
